import { ChatAdmin, ChatUser, ChatThinking } from "@pages/chatbot/components/Conversation";
import OnboardingFeatureGrid from "./OnboardingFeatureGrid";
import { CommandsList, ReconnectBar } from "@pages/chatbot/components";
import React, { useEffect, useState, useRef, useCallback, useLayoutEffect, useMemo } from "react";
import { formatChatDate } from "@utils/index";
import { useTranslation } from "react-i18next";
import { ChatAgentType } from "@constants/chat-agents";
import { LoadingOutlined } from "@ant-design/icons";
import "./Conversation.scss";
import { SenderType } from "../../../../types/chat-message";
import type { ChatMessage } from "../../../../types";
import { WelcomeIcon, ArrowDownIcon } from "@assets/icon";
import { chatService } from "../../../../services/chatService";
import type { ToastMessage } from '@hooks/useChat';
import {
  CHATBOT_TIP_ACTION_MESSAGE,
  CHATBOT_TIP_BUBBLE_MESSAGE,
  hasConsumedTipBubbleForWindow,
  markTipBubbleConsumed,
  pickRandomQuickSuggestionTips,
  pinQuickSuggestionToTop,
  type QuickSuggestion,
  type QuickSuggestionTip,
} from '@constants/onboardingFeatures';
import { safePostMessage } from '@utils/index';
import { REACT_APP_ALLOWED_DOMAINS } from '../../../../config';


interface ConversationProps {
  connected: boolean;
  handleChatSubmit: (message: string, buttonId?: number, isHidden?: boolean) => void;
  onReadyConversation: () => void;
  chatLog: any[];
  chatRef: React.RefObject<HTMLDivElement | null>;
  chatSessionVersion?: number;
  /** Tăng mỗi lần mở chatbot → refetch quick-suggestions theo quyền mới */
  quickSuggestionsRefreshKey?: number;
  /** Tăng mỗi lần đóng chatbot → thu gọn onboarding grid */
  welcomeResetKey?: number;
  hasChatHistory: boolean | null;
  activeAgent?: ChatAgentType;
  isThinking?: boolean;
  setIsThinking?: (value: boolean) => void;
  onScrollToBottom?: () => void;
  loadMoreHistory?: () => Promise<void>;
  isFetchingMore?: boolean;
  handleAgentChange?: (agent: ChatAgentType) => void;
  handleForwardToAgent?: (targetAgent: ChatAgentType, userQuestion: string | undefined, botMessage: ChatMessage | undefined, botMessageId?: string) => void;
  handleRateMessage?: (messageId: string, action: 'LIKE' | 'DISLIKE') => Promise<void>;
  handleRetryUpload?: (message: ChatMessage) => Promise<void>;
  handleQuoteMessage?: (message: ChatMessage) => void;
  scrollToQuotedMessage?: (targetMessageId: string | number) => Promise<void>;
  loadNewerHistory?: () => Promise<void>;
  needsManualReconnect?: boolean;
  isReconnecting?: boolean;
  onManualReconnect?: () => void;
  commands: any[];
  isCommandsLoading?: boolean;
  handleCommandClick: (
    command: string,
    id?: number,
    options?: { actionType?: string; actionValue?: string },
  ) => void;
  onConfirmStay?: (messageId: string, blockContents?: import('../../../../types/chat-message').BlockContent[]) => void;
  onConfirmLeave?: (messageId: string, pendingAgent: import('@constants/chat-agents').ChatAgentType) => void;
  setToastMessage?: (toast: NonNullable<ToastMessage>) => void;
}

const ALLOWED_DOMAINS: string[] = REACT_APP_ALLOWED_DOMAINS
  ? REACT_APP_ALLOWED_DOMAINS.split(',').map((d) => d.trim()).filter(Boolean)
  : ['localhost:3001', 'localhost:3000'];

const isAllowedParentOrigin = (origin: string) => {
  try {
    const url = new URL(origin);
    const originDomain = url.port ? `${url.hostname}:${url.port}` : url.hostname;
    return ALLOWED_DOMAINS.some(
      (domain) =>
        originDomain === domain ||
        url.hostname.endsWith(domain) ||
        (domain.startsWith('localhost') && url.hostname === 'localhost'),
    );
  } catch {
    return false;
  }
};


const Conversation: React.FC<ConversationProps> = ({
  handleChatSubmit,
  chatLog = [],
  chatRef,
  chatSessionVersion,
  quickSuggestionsRefreshKey = 0,
  welcomeResetKey = 0,
  hasChatHistory,
  activeAgent = ChatAgentType.AI,
  isThinking = false,
  setIsThinking,
  loadMoreHistory,
  isFetchingMore = false,
  handleAgentChange,
  handleForwardToAgent,
  handleRateMessage,
  handleRetryUpload,
  handleQuoteMessage,
  scrollToQuotedMessage,
  loadNewerHistory,
  needsManualReconnect = false,
  isReconnecting = false,
  onManualReconnect,
  commands,
  isCommandsLoading,
  handleCommandClick,
  onConfirmStay,
  onConfirmLeave,
  setToastMessage,
}) => {
  const { t } = useTranslation();
  const [userName, setUserName] = useState<string | null>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  /** Original list from GET /quick-suggestions (API order). Do not mutate on pin. */
  const [quickSuggestions, setQuickSuggestions] = useState<QuickSuggestion[]>([]);
  /** Tip currently shown on seller bubble — only affects display copy. */
  const [pinnedTip, setPinnedTip] = useState<Pick<
    QuickSuggestionTip,
    'featureKey' | 'title' | 'text'
  > | null>(null);
  const [quickSuggestionsMaxVisible, setQuickSuggestionsMaxVisible] = useState(4);
  const [isQuickSuggestionsLoading, setIsQuickSuggestionsLoading] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isFirstLoadRef = useRef(true);
  const scrollOffsetRef = useRef<number | null>(null);
  const lastUserMsgRef = useRef<HTMLDivElement | null>(null);
  const isRestoringScrollRef = useRef(false);
  const previousAgentRef = useRef<ChatAgentType | null>(null);
  const previousSessionVersionRef = useRef<number | null>(null);
  // Tracks whether we're in "question focused at top" mode
  const isQuestionFocusedRef = useRef(false);

  const displayQuickSuggestions = useMemo(() => {
    const next = pinnedTip
      ? pinQuickSuggestionToTop(quickSuggestions, pinnedTip)
      : quickSuggestions;

    if (pinnedTip) {
      console.log('[Welcome] Rearrange displayQuickSuggestions', {
        pinnedTip,
        original: quickSuggestions.map((item) => ({
          featureKey: item.featureKey,
          title: item.title,
          order: item.order,
        })),
        display: next.map((item) => ({
          featureKey: item.featureKey,
          title: item.title,
          order: item.order,
        })),
      });
    }

    return next;
  }, [quickSuggestions, pinnedTip]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollTo({ top: container.scrollHeight, behavior });
    } else if (chatRef.current) {
      chatRef.current.scrollIntoView({ behavior });
    }
  }, [chatRef]);

  // Helper: recalculate paddingBottom so question can stay at top
  // padding = max(16, clientHeight - contentBelowUserMsg)
  const recalcScrollPadding = useCallback(() => {
    const container = scrollContainerRef.current;
    const msgEl = lastUserMsgRef.current;
    if (!container || !msgEl || !isQuestionFocusedRef.current) return;

    // Content below user message = scrollHeight - current extra padding + original padding - msgBottom
    const currentPadding = parseInt(container.style.paddingBottom || '16', 10);
    const originalPadding = 16;
    const realScrollHeight = container.scrollHeight - currentPadding + originalPadding;
    const msgBottom = msgEl.offsetTop + msgEl.offsetHeight;
    const contentBelowMsg = realScrollHeight - msgBottom;

    const neededPadding = Math.max(originalPadding, container.clientHeight - contentBelowMsg);
    container.style.paddingBottom = `${neededPadding}px`;
  }, []);

  useEffect(() => {
    // Reset first load flag when agent changes
    if (previousAgentRef.current !== activeAgent || previousSessionVersionRef.current !== (chatSessionVersion ?? null)) {
      isFirstLoadRef.current = true;
      previousAgentRef.current = activeAgent;
      previousSessionVersionRef.current = chatSessionVersion ?? null;
      scrollOffsetRef.current = null;
      isRestoringScrollRef.current = false;
      // Reset question-focus mode and padding
      isQuestionFocusedRef.current = false;
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.paddingBottom = '16px';
      }
    }
    fetchUserInfo();
  }, [activeAgent, chatLog, chatSessionVersion]);

  useEffect(() => {
    // Quick suggestions + tip bubble only on Hỏi đáp (AI) agent
    if (activeAgent !== ChatAgentType.AI) {
      setQuickSuggestions([]);
      setPinnedTip(null);
      setIsQuickSuggestionsLoading(false);
      return;
    }

    if (hasChatHistory !== false) return;

    const token = sessionStorage.getItem('access_token');
    if (!token) return;

    let cancelled = false;

    const fetchQuickSuggestions = async () => {
      setIsQuickSuggestionsLoading(true);
      try {
        const response = await chatService.getQuickSuggestions(token);
        if (cancelled) return;

        const payload = response?.data;
        const suggestions = Array.isArray(payload?.suggestions)
          ? payload.suggestions
          : [];

        if (payload?.showSuggestion === false) {
          setQuickSuggestions([]);
        } else {
          setQuickSuggestions(
            [...suggestions].sort(
              (a, b) => (a.order ?? 0) - (b.order ?? 0),
            ),
          );

          // Parent tip bubble: once per morning/afternoon window (user+shop).
          // In-chat grid above still uses the full suggestions list.
          if (!hasConsumedTipBubbleForWindow(token)) {
            const tips = pickRandomQuickSuggestionTips(
              suggestions,
              3,
              payload?.totalCount,
            );
            if (tips.length > 0) {
              safePostMessage({
                type: CHATBOT_TIP_BUBBLE_MESSAGE,
                payload: { tips },
              });
              markTipBubbleConsumed(token);
            }
          }
        }

        if (typeof payload?.maxVisible === 'number' && payload.maxVisible > 0) {
          setQuickSuggestionsMaxVisible(payload.maxVisible);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[Welcome] quick suggestions failed:', error);
        }
      } finally {
        if (!cancelled) {
          setIsQuickSuggestionsLoading(false);
        }
      }
    };

    fetchQuickSuggestions();

    return () => {
      cancelled = true;
    };
  }, [hasChatHistory, activeAgent, quickSuggestionsRefreshKey]);

  const handleSuggestionClick = useCallback(
    (
      suggestion: Pick<
        QuickSuggestion,
        'featureKey' | 'title' | 'actionType' | 'actionValue'
      >,
      clickType: string = 'MINI_UI',
    ) => {
      const token = sessionStorage.getItem('access_token');
      if (token && suggestion.featureKey) {
        void chatService
          .trackQuickSuggestion(token, {
            featureKey: suggestion.featureKey,
            clickType,
            device: 'web',
          })
          .catch(() => {
            // Track is best-effort; do not block card action
          });
      }

      handleCommandClick(suggestion.title, undefined, {
        actionType: suggestion.actionType,
        actionValue: suggestion.actionValue,
      });
    },
    [handleCommandClick],
  );

  useEffect(() => {
    setPinnedTip(null);
  }, [welcomeResetKey]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const incomingType = (event.data as { type?: string } | undefined)?.type;
      if (
        incomingType === 'CHAT_TOGGLE' ||
        incomingType === CHATBOT_TIP_ACTION_MESSAGE
      ) {
        console.log('[Welcome] postMessage received (launcher / tip)', {
          origin: event.origin,
          allowed: isAllowedParentOrigin(event.origin),
          data: event.data,
        });
      }

      if (!isAllowedParentOrigin(event.origin)) return;

      const raw = (event.data || {}) as {
        type?: string;
        payload?: Partial<QuickSuggestionTip> & { description?: string; executeAction?: boolean | string };
        data?: Partial<QuickSuggestionTip> & { description?: string; executeAction?: boolean | string };
        executeAction?: boolean | string;
        featureKey?: string;
        title?: string;
        text?: string;
        description?: string;
        actionType?: string;
        actionValue?: string;
      };

      if (raw.type !== CHATBOT_TIP_ACTION_MESSAGE) return;

      const payload = raw.payload || raw.data || raw;
      const executeAction =
        payload.executeAction ?? raw.executeAction ?? raw.data?.executeAction;
      const shouldExecute = executeAction !== false && executeAction !== 'false';

      const pin = {
        featureKey: payload.featureKey || '',
        title: payload.title || '',
        text: payload.text || payload.description || '',
      };

      if (!shouldExecute) {
        console.log('[Welcome] CHATBOT_TIP_ACTION pin only (executeAction=false)', pin);
        setPinnedTip(pin);
        return;
      }

      handleSuggestionClick(
        {
          featureKey: payload.featureKey || '',
          title: payload.title || payload.text || '',
          actionType: payload.actionType || 'CHATBOT',
          actionValue: payload.actionValue || '',
        },
        'BUBBLE_EVENT',
      );
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [handleSuggestionClick]);

  const fetchUserInfo = async () => {
    try {
      if (userName === null) {
        const token = sessionStorage.getItem('access_token');
        if (token) {
          try {
            const userInfoRes = await chatService.getUserInfo(token);
            setUserName(userInfoRes?.account?.full_name || '');
          } catch (e) {
            console.error('Failed to get user info', e);
            setUserName('');
          }
        } else {
          setUserName('');
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isFirstLoadRef.current || hasChatHistory === null || !chatLog || chatLog.length === 0) return;

    const triggerScroll = () => {
      if (container.offsetHeight > 0) {
        if (!isQuestionFocusedRef.current) {
          scrollToBottom('auto');
        }

        if (chatLog.length > 0) {
          lastMessageRef.current = chatLog[chatLog.length - 1];
        }

        setTimeout(() => {
          isFirstLoadRef.current = false;
        }, 300);
      }
    };

    const ro = new ResizeObserver(() => {
      if (isFirstLoadRef.current) {
        triggerScroll();
      } else if (!isQuestionFocusedRef.current && container && !isFetchingMore) {
        // Only auto-scroll when NOT in question-focus mode
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 250;
        if (isNearBottom) {
          scrollToBottom('auto');
        }
      }
    });
    ro.observe(container);

    triggerScroll();
    const timers = [
      setTimeout(triggerScroll, 100),
      setTimeout(triggerScroll, 500),
    ];

    return () => {
      ro.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [chatLog?.length, hasChatHistory, activeAgent, scrollToBottom]);

  useLayoutEffect(() => {
    // Restore scroll position after loading more history
    if (scrollOffsetRef.current !== null && scrollContainerRef.current && !isFetchingMore) {
      const container = scrollContainerRef.current;
      const scrollOffset = scrollOffsetRef.current;

      // Wait for DOM to update, then restore scroll
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          const newScrollHeight = scrollContainerRef.current.scrollHeight;
          // Calculate new scroll position: maintain distance from bottom
          const newScrollTop = newScrollHeight - scrollOffset - container.clientHeight;
          container.scrollTop = Math.max(0, newScrollTop);
          scrollOffsetRef.current = null;
          isRestoringScrollRef.current = false;
        }
      });
      return; // Don't proceed to auto-scroll logic when restoring
    }
  }, [chatLog, isFetchingMore]);

  const lastMessageRef = useRef<any>(null);

  useLayoutEffect(() => {
    if (
      isFetchingMore ||
      scrollOffsetRef.current !== null ||
      !chatLog ||
      chatLog.length === 0
    ) {
      return;
    }

    const container = scrollContainerRef.current;
    if (!container) return;

    const lastMsg = chatLog[chatLog.length - 1];

    if (isFirstLoadRef.current) {
      return;
    }
    if (lastMsg === lastMessageRef.current && !isThinking) return;

    lastMessageRef.current = lastMsg;

    const isStreaming = lastMsg?.senderType === SenderType.BOT && lastMsg?.isFinal === false;

    // === CSKH: keep original scroll behavior (always scroll to bottom) ===
    if (activeAgent === ChatAgentType.CSKH) {
      if (isStreaming) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 250;
        if (isNearBottom) {
          scrollToBottom('auto');
        }
      } else {
        scrollToBottom('smooth');
      }
      return;
    }

    // === USER MESSAGE: scroll question to TOP (non-CSKH agents) ===
    if (lastMsg?.senderType === SenderType.USER && !lastMsg?.isHidden && !lastMsg?._isHistory) {
      isQuestionFocusedRef.current = true;

      // Add generous paddingBottom so there's room to scroll question to top
      container.style.paddingBottom = `${container.clientHeight}px`;

      requestAnimationFrame(() => {
        if (lastUserMsgRef.current && scrollContainerRef.current) {
          const c = scrollContainerRef.current;
          const cRect = c.getBoundingClientRect();
          const mRect = lastUserMsgRef.current.getBoundingClientRect();
          const targetScrollTop = c.scrollTop + (mRect.top - cRect.top);
          c.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
        }
      });
      return;
    }

    // === BOT MESSAGE (socket / stream answer) → always scroll to bottom ===
    if (lastMsg?.senderType === SenderType.BOT) {
      isQuestionFocusedRef.current = false;
      if (container.style.paddingBottom && container.style.paddingBottom !== '16px') {
        container.style.paddingBottom = '16px';
      }
      if (isStreaming) {
        scrollToBottom('auto');
      } else {
        scrollToBottom('smooth');
      }
      return;
    }

    // Fallback (system / other): scroll to bottom
    if (isStreaming) {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 250;
      if (isNearBottom) {
        scrollToBottom('auto');
      }
    } else {
      scrollToBottom('smooth');
    }
  }, [chatLog, isThinking, scrollToBottom, isFetchingMore, recalcScrollPadding, activeAgent]);

  // Recalc padding when thinking indicator appears/disappears (rendered outside chatLog)
  useEffect(() => {
    if (!isQuestionFocusedRef.current || activeAgent === ChatAgentType.CSKH) return;
    // Wait for DOM to render the thinking indicator
    const id = requestAnimationFrame(() => {
      recalcScrollPadding();
    });
    return () => cancelAnimationFrame(id);
  }, [isThinking, recalcScrollPadding, activeAgent]);

  // Cuộn xuống bottom khi form Reconnect xuất hiện
  useEffect(() => {
    if (needsManualReconnect) {
      // Tắt thinking khi mất kết nối
      if (isThinking && setIsThinking) {
        setIsThinking(false);
      }
      // Scroll xuống để hiện form Reconnect
      setTimeout(() => {
        scrollToBottom('smooth');
      }, 100);
    }
  }, [needsManualReconnect]);



  const handleScroll = useCallback(() => {
    const element = scrollContainerRef.current;
    if (!element || isFetchingMore) return;

    // Scroll UP → load older messages
    if (element.scrollTop <= 5 && loadMoreHistory) {
      const distFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      scrollOffsetRef.current = distFromBottom;
      isRestoringScrollRef.current = true;
      loadMoreHistory().catch(() => {
        scrollOffsetRef.current = null;
        isRestoringScrollRef.current = false;
      });
    }

    // Toggle scroll down button: exclude extra padding from question-focus mode
    const distanceFromBottomForBtn = element.scrollHeight - element.scrollTop - element.clientHeight;
    const extraPadding = isQuestionFocusedRef.current
      ? Math.max(0, parseInt(element.style.paddingBottom || '16', 10) - 16)
      : 0;
    setShowScrollDown((distanceFromBottomForBtn - extraPadding) > 150);

    // Scroll DOWN → load newer messages (CSKH only, when previousCursor exists)
    if (loadNewerHistory && activeAgent === ChatAgentType.CSKH) {
      const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      if (distanceFromBottom <= 5) {
        loadNewerHistory().catch(() => { });
      }
    }
  }, [loadMoreHistory, loadNewerHistory, isFetchingMore, activeAgent]);

  // `chatLog` is already deduplicated by `useChatHistory`

  if (hasChatHistory === null) {
    return (
      <div className="loading-indicator">
        <LoadingOutlined className="loading-indicator-icon" />
        <span className="loading-indicator-text">Đang tải</span>
      </div>
    );
  }

  // Filter out hidden messages from the log before rendering
  const filteredChatLog = (chatLog || []).filter((msg: any) => !msg.isHidden);

  // Find the actual index of the last user message in order to scroll to the correct position
  let actualLastUserIndex = -1;
  for (let i = filteredChatLog.length - 1; i >= 0; i--) {
    if (filteredChatLog[i].user === SenderType.USER) {
      actualLastUserIndex = i;
      break;
    }
  }

  const renderMessage = (entry: any, index: number) => {
    const uniqueKey = entry._uniqueKey || entry.messageId || entry.id || `msg-${index}`;
    const isLastUser = index === actualLastUserIndex;

    const currentDate = formatChatDate(entry.time);
    const prevDate = index > 0 ? formatChatDate(filteredChatLog[index - 1].time) : null;
    const showDateDivider = Boolean(currentDate && (index === 0 || currentDate !== prevDate));

    return (
      <React.Fragment key={uniqueKey}>
        {showDateDivider && (
          <div className="date-divider text-center my-4 font-base">
            <span className="text-sm bg-[var(--colour-semantic-neutral-surface)] text-[var(--colour-semantic-neutral-shade-md)] px-2 py-1.5 rounded-full">{currentDate}</span>
          </div>
        )}
        <div
          id={`message-${entry.messageId || entry.id}`}
          data-message-id={entry.messageId || ''}
          data-id={entry.id || ''}
          ref={isLastUser ? lastUserMsgRef : null}
        >
          {entry.user === SenderType.USER ? (
            <ChatUser
              message={entry.text}
              isRead={entry?.isRead}
              time={entry?.time}
              messageId={entry.messageId}
              uploadStatus={entry.uploadStatus}
              uploadError={entry.uploadError}
              fullMessage={entry}
              onRetryUpload={handleRetryUpload}
              onQuote={handleQuoteMessage}
              activeAgent={activeAgent}
              isQueuing={entry.isQueuing}
              scrollToQuotedMessage={scrollToQuotedMessage}
            />
          ) : (
            <ChatAdmin
              onQuestionClick={handleChatSubmit}
              messageId={entry.messageId?.toString()}
              onButtonClick={(msg: string, btnId: number, isHidden?: boolean) => handleChatSubmit(msg, btnId, isHidden)}
              onThinkingStop={() => setIsThinking?.(false)}
              chatMessage={entry}
              message={entry.text}
              handleAgentChange={handleAgentChange}
              isLastMessage={index === filteredChatLog.length - 1}
              handleForwardToAgent={handleForwardToAgent}
              handleRateMessage={handleRateMessage}
              onQuote={handleQuoteMessage}
              activeAgent={activeAgent}
              onConfirmStay={onConfirmStay}
              onConfirmLeave={onConfirmLeave}
              setToastMessage={setToastMessage}
            />
          )}
        </div>
      </React.Fragment>
    );
  };

  const renderMessagesOrEmptyState = () => {
    if (filteredChatLog.length > 0) {
      return filteredChatLog.map(renderMessage);
    }

    if (hasChatHistory === false) {
      return (
        <React.Fragment>
          <div className="flex gap-2 mt-[56px] items-center text-sm text-[var(--colour-primary-neutral-shade-md)]">
            <WelcomeIcon /> {userName ? `Xin chào ${userName}!` : 'Xin chào bạn!'}
          </div>
          <div className="flex items-center font-semibold text-[28px] text-[var(--colour-primary-neutral-shade-md)]">
           {t("common.welcomeMessage")}
          </div>

          {/* AI Hỏi đáp: onboarding cards from quick-suggestions API */}
          {activeAgent === ChatAgentType.AI && (
            <OnboardingFeatureGrid
              suggestions={displayQuickSuggestions}
              maxVisible={quickSuggestionsMaxVisible}
              resetKey={welcomeResetKey}
              onSuggestionClick={handleSuggestionClick}
              isLoading={isQuickSuggestionsLoading}
              isThinking={isThinking}
            />
          )}

          {/* Other agents: default command quick actions (same as main) */}
          {activeAgent !== ChatAgentType.AI &&
            (isCommandsLoading || (commands && commands.length > 0)) && (
            <CommandsList
              commands={commands}
              onCommandClick={handleCommandClick}
              isLoading={isCommandsLoading}
              isThinking={isThinking}
            />
          )}
        </React.Fragment>
      );
    }

    return null;
  };

  return (
    <div className="conversation-wrapper">
      <div className="messager-container">
        <div
          className="chat-messages"
            ref={scrollContainerRef}
            onScroll={handleScroll}
          >
            {isFetchingMore && (
              <div className="loading-more text-center py-2 text-xs text-gray-400">
                {t('common.loadingMore')}
              </div>
            )}

            {renderMessagesOrEmptyState()}
            {isThinking && <ChatThinking />}

            {/* Reconnect message - renders like a ChatAdmin bubble at the bottom */}
            {needsManualReconnect && onManualReconnect && (
              <div className="chat-admin flex flex-col mt-[10px] w-full">
                      <ReconnectBar
                        onReconnect={onManualReconnect}
                        isConnecting={isReconnecting}
                      />
              </div>
            )}

            <div ref={chatRef} style={{ height: '1px' }}></div>
        </div >
        
        {/* Floating Scroll to Bottom Button */}
        {showScrollDown && (
          <button
            className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-white rounded-full w-8 h-8 flex items-center justify-center  z-10 transition-transform hover:scale-105 border border-[var(--colour-semantic-neutral-light-xxl)]"
            onClick={() => scrollToBottom('smooth')}
            aria-label="Cuộn xuống dưới"
            type="button"
          >
            <ArrowDownIcon className="w-4 h-4" />
          </button>
        )}
      </div >
    </div >
  );
};

export default Conversation;
